// Mock data shaped exactly like the SFB-1105 backend DTOs
// (ChampionshipOverviewDTO, ChampionshipBracketDTO, ChampionshipMatchDTO,
//  ChampionshipParticipantViewDTO). Used by championship.service.js until the
// backend feature branch lands on dev. Keep field names in sync with the
// real DTOs so the UI doesn't need to change when we flip to live data.

const STAGE_TYPES = ['ROUND_OF_16', 'QUARTERFINALS', 'SEMIFINALS', 'FINALS'];

function makeMockOverview(championshipId = 9999) {
  const baseDate = new Date('2026-04-22T00:00:00Z');
  const stages = STAGE_TYPES.map((stageType, idx) => ({
    id: 7000 + idx,
    tournamentId: 8000 + idx,
    stageOrder: idx + 1,
    stageType,
    advancementRule: 'TOP_N',
    advanceCount: stageType === 'FINALS' ? 1 : null,
    tieBreakerRule: 'HIGHEST_SCORE',
    startDate: new Date(baseDate.getTime() + idx * 7 * 24 * 60 * 60 * 1000).toISOString(),
    endDate: new Date(baseDate.getTime() + (idx + 1) * 7 * 24 * 60 * 60 * 1000).toISOString(),
    tournamentState: idx <= 1 ? 'FINISHED' : (idx === 2 ? 'RUNNING' : 'NOT_STARTED'),
    advancementCompletedAt: idx <= 1 ? new Date(baseDate.getTime() + (idx + 1) * 7 * 24 * 60 * 60 * 1000).toISOString() : null,
  }));
  return {
    id: championshipId,
    name: 'Championship Copa América 2024',
    description: 'Mock championship — single elimination bracket of 16 participants.',
    slug: 'championship-copa-america-2024',
    visibility: 'PRIVATE',
    status: 'RUNNING',
    startDate: stages[0].startDate,
    endDate: stages[stages.length - 1].endDate,
    ownerClubId: 132,
    stages,
  };
}

const PARTICIPANT_NAMES = [
  'Carlos Sainz', 'Lando Norris', 'Max Verstappen', 'Lewis Hamilton',
  'Charles Leclerc', 'George Russell', 'Fernando Alonso', 'Sebastian Vettel',
  'Valtteri Bottas', 'Esteban Ocon', 'Pierre Gasly', 'Daniel Ricciardo',
  'Sergio Perez', 'Kimi Raikkonen', 'Mick Schumacher', 'Yuki Tsunoda',
];

function makeMockParticipants() {
  return PARTICIPANT_NAMES.map((name, i) => {
    // First 8 advanced past round-of-16 in this mock; first 4 past quarters;
    // first 2 past semis; #1 won the (yet-to-finish) final.
    let status = 'ELIMINATED';
    let currentStageOrder = 1;
    let eliminatedAtStageOrder = 1;
    if (i < 2) { status = 'ACTIVE'; currentStageOrder = 4; eliminatedAtStageOrder = null; }
    else if (i < 4) { status = 'ELIMINATED'; eliminatedAtStageOrder = 3; currentStageOrder = 3; }
    else if (i < 8) { status = 'ELIMINATED'; eliminatedAtStageOrder = 2; currentStageOrder = 2; }
    return {
      userId: 5000 + i,
      login: name,
      status,
      currentStageOrder,
      eliminatedAtStageOrder,
      seed: i + 1,
    };
  });
}

function makeMockBracket(championshipId = 9999) {
  // 16 → 8 → 4 → 2 single-elimination tree.
  // matchOrder is left-to-right within a stage (visual ordering).
  // nextMatchId / nextMatchSlot wires the tree (winner of [s1.m1] feeds [s2.m1].A, [s1.m2] feeds [s2.m1].B, etc.)
  const overview = makeMockOverview(championshipId);
  const participants = makeMockParticipants();
  const matches = [];
  let matchIdCounter = 9000;

  // Round of 16: 8 matches
  const r16Matches = [];
  for (let i = 0; i < 8; i++) {
    const a = participants[i * 2];
    const b = participants[i * 2 + 1];
    const winner = a.status === 'ACTIVE' || a.eliminatedAtStageOrder > 1 ? a : b;
    r16Matches.push({
      id: matchIdCounter++,
      stageId: overview.stages[0].id,
      matchOrder: i + 1,
      participantAId: 6000 + i * 2,
      participantBId: 6000 + i * 2 + 1,
      userAId: a.userId,
      userBId: b.userId,
      winnerParticipantId: winner === a ? 6000 + i * 2 : 6000 + i * 2 + 1,
      winnerUserId: winner.userId,
      scoreA: 100 + Math.random() * 50,
      scoreB: 100 + Math.random() * 50,
      status: 'COMPLETED',
      nextMatchId: null, // wired below
      nextMatchSlot: i % 2 === 0 ? 'A' : 'B',
    });
  }

  // Quarterfinals: 4 matches
  const qfMatches = [];
  for (let i = 0; i < 4; i++) {
    const aWinnerUser = participants.find((p) => p.userId === r16Matches[i * 2].winnerUserId);
    const bWinnerUser = participants.find((p) => p.userId === r16Matches[i * 2 + 1].winnerUserId);
    const winner = aWinnerUser.eliminatedAtStageOrder == null || aWinnerUser.eliminatedAtStageOrder > 2 ? aWinnerUser : bWinnerUser;
    qfMatches.push({
      id: matchIdCounter++,
      stageId: overview.stages[1].id,
      matchOrder: i + 1,
      participantAId: 6100 + i * 2,
      participantBId: 6100 + i * 2 + 1,
      userAId: aWinnerUser.userId,
      userBId: bWinnerUser.userId,
      winnerParticipantId: winner === aWinnerUser ? 6100 + i * 2 : 6100 + i * 2 + 1,
      winnerUserId: winner.userId,
      scoreA: 100 + Math.random() * 50,
      scoreB: 100 + Math.random() * 50,
      status: 'COMPLETED',
      nextMatchId: null,
      nextMatchSlot: i % 2 === 0 ? 'A' : 'B',
    });
    r16Matches[i * 2].nextMatchId = qfMatches[i].id;
    r16Matches[i * 2 + 1].nextMatchId = qfMatches[i].id;
  }

  // Semifinals: 2 matches — one COMPLETED, one IN_PROGRESS for variety.
  const sfMatches = [];
  for (let i = 0; i < 2; i++) {
    const aWinnerUser = participants.find((p) => p.userId === qfMatches[i * 2].winnerUserId);
    const bWinnerUser = participants.find((p) => p.userId === qfMatches[i * 2 + 1].winnerUserId);
    const completed = i === 0;
    const winner = completed ? aWinnerUser : null;
    sfMatches.push({
      id: matchIdCounter++,
      stageId: overview.stages[2].id,
      matchOrder: i + 1,
      participantAId: 6200 + i * 2,
      participantBId: 6200 + i * 2 + 1,
      userAId: aWinnerUser.userId,
      userBId: bWinnerUser.userId,
      winnerParticipantId: completed ? 6200 + i * 2 : null,
      winnerUserId: winner ? winner.userId : null,
      scoreA: completed ? 130 : null,
      scoreB: completed ? 110 : null,
      status: completed ? 'COMPLETED' : 'IN_PROGRESS',
      nextMatchId: null,
      nextMatchSlot: i % 2 === 0 ? 'A' : 'B',
    });
    qfMatches[i * 2].nextMatchId = sfMatches[i].id;
    qfMatches[i * 2 + 1].nextMatchId = sfMatches[i].id;
  }

  // Final: 1 match — PENDING (only one semifinalist known so far).
  const finalMatch = {
    id: matchIdCounter++,
    stageId: overview.stages[3].id,
    matchOrder: 1,
    participantAId: 6300,
    participantBId: null,
    userAId: sfMatches[0].winnerUserId,
    userBId: null,
    winnerParticipantId: null,
    winnerUserId: null,
    scoreA: null,
    scoreB: null,
    status: 'PENDING',
    nextMatchId: null,
    nextMatchSlot: null,
  };
  sfMatches[0].nextMatchId = finalMatch.id;
  sfMatches[1].nextMatchId = finalMatch.id;

  matches.push(...r16Matches, ...qfMatches, ...sfMatches, finalMatch);

  return {
    championshipId,
    matches,
  };
}

export default {
  makeMockOverview,
  makeMockBracket,
  makeMockParticipants,
};
